/**
 * Create the Look – Cross-sell product cards
 *
 * <create-the-look> is the parent container that fetches cross-sell
 * products from the external API and creates <create-the-look-product>
 * elements for each returned product.
 *
 * Each <create-the-look-product> renders a mini product card with
 * variant selection & add-to-cart functionality.
 */

const CTL_API_BASE_DEFAULT = 'https://shopifycrosssellapp.testarc.com.au/api/products/crossell';

/* ==================================================================
   Parent element – fetches products from the cross-sell API
   ================================================================== */
if (!customElements.get('create-the-look')) {
  class CreateTheLook extends HTMLElement {
    connectedCallback() {
      this.productId = this.getAttribute('data-product-id');
      this.shop = this.getAttribute('data-shop');
      this.apiBase = this.getAttribute('data-api-base') || CTL_API_BASE_DEFAULT;
      this.translationsRaw = this.getAttribute('data-translations') || '{}';
      this.grid = this.querySelector('[data-grid]');
      this.sectionId = this.getAttribute('data-section-id') || '';
      this.layoutMobile = this.getAttribute('data-layout-mobile') || 'grid';
      this.isSlider = this.layoutMobile === 'carousel';

      if (!this.productId || !this.shop) return;
      this.fetchCrossSellProducts();
    }

    async fetchCrossSellProducts() {
      try {
        const url = `${this.apiBase}?shop=${encodeURIComponent(this.shop)}&product_id=${encodeURIComponent(this.productId)}`;
        const response = await fetch(url);

        if (!response.ok) {
          console.error('CreateTheLook: API returned', response.status);
          this.hideIfEmpty();
          return;
        }

        const data = await response.json();
        const products = Array.isArray(data) ? data : data.products || data.data || [];

        if (!products.length) {
          this.hideIfEmpty();
          return;
        }

        this.renderProducts(products);
      } catch (err) {
        console.error('CreateTheLook: failed to fetch cross-sell products', err);
        this.hideIfEmpty();
      }
    }

    renderProducts(products) {
      if (!this.grid) return;

      products.forEach((product, index) => {
        const el = document.createElement('create-the-look-product');
        el.setAttribute('data-product', JSON.stringify(product));
        el.setAttribute('data-translations', this.translationsRaw);

        if (this.isSlider) {
          // Wrap each product in a <li> for slider-component compatibility
          const li = document.createElement('li');
          li.id = `Slide-${this.sectionId}-${index + 1}`;
          li.className = 'grid__item slider__slide';
          li.appendChild(el);
          this.grid.appendChild(li);
        } else {
          const wrapper = document.createElement('div');
          wrapper.className = 'grid__item';
          wrapper.appendChild(el);
          this.grid.appendChild(wrapper);
        }
      });

      // Update slider totals and re-initialize slider-component
      if (this.isSlider) {
        this.initSlider(products.length);
      }

      this.showColumn();
    }

    /**
     * Initialize or re-initialize the slider-component after dynamic content is added.
     */
    initSlider(totalSlides) {
      // Update counter total
      const counterTotal = this.querySelector('.slider-counter--total');
      if (counterTotal) counterTotal.textContent = totalSlides;

      // Update scrollbar track
      const scrollbarTrack = this.querySelector('.slider-scrollbar__track');
      if (scrollbarTrack) {
        scrollbarTrack.style.setProperty('--num-slides', totalSlides);
        scrollbarTrack.setAttribute('data-num-slides', totalSlides);
      }

      // Re-initialize the slider-component custom element
      const sliderComponent = this.querySelector('slider-component');
      if (sliderComponent) {
        // If slider-component has already been initialized (has a slider property),
        // call resetPages() to re-scan the dynamically added slides.
        // Otherwise, call init() to run the full first-time initialization.
        if (sliderComponent.slider) {
          if (typeof sliderComponent.resetPages === 'function') {
            sliderComponent.resetPages();
          }
        } else if (typeof sliderComponent.init === 'function') {
          sliderComponent.init();
        }
      }
    }

    /**
     * Show the dedicated CTL column wrapper (if rendered as a block in main-product)
     * and add the layout class to the product grid.
     */
    showColumn() {
      const columnWrapper = this.closest('[data-ctl-column]');
      if (columnWrapper) {
        columnWrapper.removeAttribute('hidden');
        const productGrid = columnWrapper.closest('.product.grid');
        if (productGrid) {
          productGrid.classList.add('product--has-ctl');
        }
      }
    }

    hideIfEmpty() {
      // Hide the entire element when no products are returned
      this.style.display = 'none';
      // Ensure the column wrapper stays hidden
      const columnWrapper = this.closest('[data-ctl-column]');
      if (columnWrapper) {
        columnWrapper.setAttribute('hidden', '');
        const productGrid = columnWrapper.closest('.product.grid');
        if (productGrid) {
          productGrid.classList.remove('product--has-ctl');
        }
      }
    }
  }

  customElements.define('create-the-look', CreateTheLook);
}

/* ==================================================================
   Child element – individual product card
   ================================================================== */
if (!customElements.get('create-the-look-product')) {
  class CreateTheLookProduct extends HTMLElement {
    constructor() {
      super();

      this.product = null;
      this.translations = {};
      this.currentVariant = null;
      this.isSubmitting = false;
      this.productFromShopify = null;
      this.isAllVariantsOnSale = false;
    }

    connectedCallback() {
      try {
        this.product = JSON.parse(this.getAttribute('data-product'));
      } catch (e) {
        console.error('CreateTheLookProduct: invalid data-product JSON', e);
        return;
      }

      try {
        this.translations = JSON.parse(this.getAttribute('data-translations'));
      } catch (e) {
        this.translations = {};
      }

      if (!this.product) return;

      // If single variant, pre-select it
      if (this.product.variants && this.product.variants.length === 1) {
        this.currentVariant = this.product.variants[0];
      }

      this.render();
      this.fetchProductData();
    }

    /* ------------------------------------------------------------------
       Fetch full product data from Shopify's .js endpoint
    ------------------------------------------------------------------ */
    async fetchProductData() {
      if (!this.product || !this.product.handle) return;

      try {
        const response = await fetch(`${window.location.origin}/products/${this.product.handle}.js`);
        this.productFromShopify = await response.json();
        this.checkIfAllVariantsOnSale();
        this.renderPrice();
      } catch (err) {
        console.error('CreateTheLookProduct: failed to fetch product', err);
      }
    }

    /* ------------------------------------------------------------------
       Price helpers
    ------------------------------------------------------------------ */
    formatMoney(cents) {
      if (typeof cents !== 'number') return '';
      const amount = (cents / 100).toFixed(2);
      // Use Shopify money format if available, otherwise basic format
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents, window.theme?.settings?.moneyWithCurrencyFormat || '${{amount}}');
      }
      return `$${amount}`;
    }

    checkIfAllVariantsOnSale() {
      if (!this.productFromShopify) return;
      this.isAllVariantsOnSale = this.productFromShopify.variants.every(
        (v) => v.compare_at_price > 0
      );
    }

    checkIfLowestPricedVariantOnSale() {
      if (!this.productFromShopify) return false;
      const lowestPricedVariant = this.productFromShopify.variants.find(
        (v) => v.price === this.productFromShopify.price && v.compare_at_price > 0
      );
      return lowestPricedVariant?.compare_at_price > 0;
    }

    checkIfHighestPricedVariantOnSale() {
      if (!this.productFromShopify) return false;
      const highestPricedVariant = this.productFromShopify.variants.find(
        (v) => v.price === this.productFromShopify.price_max && v.compare_at_price > 0
      );
      return highestPricedVariant?.compare_at_price > 0;
    }

    /* ------------------------------------------------------------------
       Wishlist
    ------------------------------------------------------------------ */
    toggleWishlist(event) {
      const btn = event.currentTarget;
      const variantId = this.currentVariant
        ? this.currentVariant.id
        : this.product.variants[0]?.id;

      if (!variantId) return;

      if (btn.classList.contains('iwishAdded')) {
        if (typeof iwish_remove === 'function') {
          iwish_remove($(btn), variantId);
        }
        btn.classList.remove('iwishAdded');
      } else {
        if (typeof iwish_addCollection === 'function') {
          iwish_addCollection($(btn), variantId);
        }
        btn.classList.add('iwishAdded');
      }
    }

    /**
     * After the card is in the DOM, trigger iWish's global check so
     * already-wishlisted products get the .iwishAdded class.
     */
    initWishlist() {
      if (typeof iWishVerify === 'function') {
        try { iWishVerify(); } catch (_) { /* iWish not ready */ }
      }
    }

    /* ------------------------------------------------------------------
       Variant selection
    ------------------------------------------------------------------ */
    setVariant(variant) {
      this.currentVariant = variant;

      // Update active state on variant buttons
      const buttons = this.querySelectorAll('.ctl-product-card__variant-btn');
      buttons.forEach((btn) => {
        const btnVariantId = parseInt(btn.getAttribute('data-variant-id'), 10);
        btn.classList.toggle('active', btnVariantId === variant.id);
      });

      // Update ATC button state
      this.updateAtcButton();
    }

    updateAtcButton() {
      const btn = this.querySelector('.ctl-product-card__atc');
      if (!btn) return;

      const textEl = btn.querySelector('.ctl-atc-text');
      if (!this.currentVariant || this.currentVariant.inventory_qty < 1) {
        btn.setAttribute('disabled', 'disabled');
        if (textEl) {
          textEl.textContent =
            this.currentVariant && this.currentVariant.inventory_qty < 1
              ? this.translations.sold_out || 'Sold out'
              : this.translations.add || 'Add';
        }
      } else {
        btn.removeAttribute('disabled');
        if (textEl) {
          textEl.textContent = this.translations.add || 'Add';
        }
      }
    }

    /* ------------------------------------------------------------------
       Add to cart – mirrors product-form.js behavior
    ------------------------------------------------------------------ */
    addToCart() {
      if (this.isSubmitting || !this.currentVariant) return;

      const btn = this.querySelector('.ctl-product-card__atc');
      this.isSubmitting = true;
      if (btn) {
        btn.classList.add('is-loading');
        btn.setAttribute('aria-disabled', 'true');
      }

      const cartDrawer = document.querySelector('cart-drawer');
      const cartItems = document.querySelector('cart-items');
      const cartNotification = document.querySelector('cart-notification');

      // Build FormData the same way product-form.js does
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('id', this.currentVariant.id);
      formData.append('quantity', 1);

      // Collect section IDs for section rendering (cart drawer, notification, cart items)
      let sections = [];
      if (cartDrawer) {
        sections = cartDrawer.getSectionsToRender().map((section) => section.id);
      }
      if (cartNotification) {
        window.loadTemplateContent(cartNotification);
        const cartNotificationSections = cartNotification.getSectionsToRender().map((section) => section.id);
        sections = [...sections, ...cartNotificationSections];
      }

      if (cartItems) {
        const cartItemsSections = cartItems.getSectionsToRender().map((section) => section.section);
        sections = [...sections, ...cartItemsSections];
      }

      if (sections.length > 0) {
        formData.append('sections', [...new Set(sections)]);
        formData.append('sections_url', window.location.pathname);

        if (theme.settings.cartDrawer.atcAction === 'drawer' && cartDrawer) {
          cartDrawer.setActiveElement(document.activeElement);
        } else if (theme.settings.cartDrawer.atcAction === 'notification' && cartNotification) {
          cartNotification.setActiveElement(document.activeElement);
        }
      }

      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            // Error from Shopify
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: this.currentVariant.id,
              errors: response.errors || response.description,
              message: response.message,
            });
            console.error('Add to cart error:', response.description);
            alert(response.description || 'An error occurred while adding to cart.');
            if (cartDrawer) cartDrawer.reload();
            return;
          } else if (theme.settings.cartDrawer.atcAction === 'page') {
            window.location = window.routes.cart_url;
            return;
          }

          if (response.sections === null && document.querySelector('.section-main-cart')) {
            window.location.reload();
          }

          // Publish cart update event (source: 'product-form' so the theme maps it to cartAdd)
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: this.currentVariant.id,
            cartData: response,
          });

          // Render cart drawer / cart items with the returned section HTML
          if (cartDrawer) cartDrawer.renderContents(response);
          if (cartItems) cartItems.renderContents(response);
          if (cartNotification) cartNotification.renderContentsNotificationProduct(response);
          
          if (btn) {
            const textEl = btn.querySelector('.ctl-atc-text');
            if (textEl) textEl.textContent = this.translations.added || 'Added';
            btn.setAttribute('disabled', 'disabled');
            btn.classList.add('ctl-product-card__atc--added');
          }
        })
        .catch((e) => {
          console.error('Add to cart error:', e);
        })
        .finally(() => {
          this.isSubmitting = false;
          if (btn) {
            btn.classList.remove('is-loading');
            btn.removeAttribute('aria-disabled');
          }
          if (cartDrawer && cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
          if (cartNotification && cartNotification.classList.contains('is-empty')) cartNotification.classList.remove('is-empty');
        });
    }

    /* ------------------------------------------------------------------
       Render price (called after product data is fetched)
    ------------------------------------------------------------------ */
    renderPrice() {
      const container = this.querySelector('.ctl-product-card__price');
      if (!container || !this.productFromShopify) return;

      const p = this.productFromShopify;
      let html = '';

      const hasCompareAtPrice = p.compare_at_price_max > p.price;
      const priceVaries = p.price !== p.price_max;
      const lowestOnSale = this.checkIfLowestPricedVariantOnSale();
      const highestOnSale = this.checkIfHighestPricedVariantOnSale();

      // Strike-through (compare at)
      if (hasCompareAtPrice && lowestOnSale && !highestOnSale) {
        // Partial sale – no strikethrough
      } else if (hasCompareAtPrice && !this.isAllVariantsOnSale) {
        // Not all on sale – no strikethrough
      } else if (hasCompareAtPrice) {
        html += `<s>${this.formatMoney(p.compare_at_price_max)}</s> `;
      }

      // Actual price
      if (priceVaries && p.compare_at_price > 0 && lowestOnSale && !highestOnSale) {
        html += `From <span class="ctl-price--sale">${this.formatMoney(p.price)}</span>`;
        html += ` – ${this.formatMoney(p.price_max)}`;
      } else if (priceVaries && !this.isAllVariantsOnSale) {
        html += `From ${this.formatMoney(p.price)} – ${this.formatMoney(p.price_max)}`;
      } else if (priceVaries && this.isAllVariantsOnSale) {
        html += `<span class="${hasCompareAtPrice ? 'ctl-price--sale' : ''}">From ${this.formatMoney(p.price)} – ${this.formatMoney(p.price_max)}</span>`;
      } else {
        html += `<span class="${hasCompareAtPrice ? 'ctl-price--sale' : ''}">${this.formatMoney(p.price)}</span>`;
      }

      container.innerHTML = html;
    }

    /* ------------------------------------------------------------------
       Initial render
    ------------------------------------------------------------------ */
    render() {
      const product = this.product;
      if (!product) return;

      const hasBadge = product.tags && product.tags.badge;
      const hasSalesInfo = product.tags && product.tags.sales_info;
      const hasMultipleVariants = product.variants && product.variants.length > 1;
      const featuredImage = product.featured_image || '';
      const productUrl = `/products/${product.handle}`;

      let variantsHtml = '';
      if (hasMultipleVariants) {
        variantsHtml = `<ul class="ctl-product-card__variants">`;
        product.variants.forEach((variant) => {
          const soldOutClass = variant.inventory_qty < 1 ? ' sold-out' : '';
          const activeClass =
            this.currentVariant && this.currentVariant.id === variant.id ? ' active' : '';
          variantsHtml += `
            <li>
              <button type="button"
                class="ctl-product-card__variant-btn${activeClass}${soldOutClass}"
                data-variant-id="${variant.id}"
              >${variant.title}</button>
            </li>`;
        });
        variantsHtml += `</ul>`;
      }

      // Determine initial ATC label
      let atcLabel = '';
      let atcDisabled = '';
      if (!this.currentVariant) {
        atcLabel = this.translations.add || 'Add';
        atcDisabled = ' disabled';
      } else if (this.currentVariant.inventory_qty < 1) {
        atcLabel = this.translations.sold_out || 'Sold out';
        atcDisabled = ' disabled';
      } else {
        atcLabel = this.translations.add || 'Add';
      }

      this.innerHTML = `
        <div class="ctl-product-card">
          <div class="ctl-product-card__row-top">
            <div class="ctl-product-card__media">
              ${hasBadge ? `<span class="ctl-product-card__badge hidden">${product.tags.badge}</span>` : ''}
              <div class="ctl-product-card__wishlist wishlist wishlist--small">
                <a class="wishlist__button iWishAddColl iwishcheck"
                  href="javascript:void(0);"
                  data-variant="${product.variants[0]?.id || ''}"
                  data-product="${product.id}"
                  data-ptitle="${product.title}"
                  aria-label="Add to wishlist"
                ></a>
              </div>
              <a href="${productUrl}">
                <img loading="lazy"
                  src="${featuredImage}"
                  alt="${product.title}"
                  width="288"
                  height="360"
                />
              </a>
            </div>

            <div class="ctl-product-card__info">
              <h3 class="ctl-product-card__title">
                <a href="${productUrl}">${product.title}</a>
              </h3>

              <div class="ctl-product-card__price">
                <!-- Populated after fetch -->
              </div>

              ${variantsHtml}

              ${hasSalesInfo ? `<div class="ctl-product-card__sales-info hidden">${product.tags.sales_info}</div>` : ''}
            </div>
          </div>

          <div class="ctl-product-card__row-bottom">
            <button type="button" class="ctl-product-card__atc"${atcDisabled}>
              <span class="ctl-atc-text">${atcLabel}</span>
              <span class="ctl-spinner"></span>
            </button>
            <div class="ctl-product-card__view-product">
              <a href="${productUrl}">View Product</a>
            </div>
          </div>
        </div>
      `;

      this.bindEvents();
    }

    /* ------------------------------------------------------------------
       Event binding
    ------------------------------------------------------------------ */
    bindEvents() {
      // Variant buttons
      this.querySelectorAll('.ctl-product-card__variant-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          const variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
          const variant = this.product.variants.find((v) => v.id === variantId);
          if (variant) this.setVariant(variant);
        });
      });

      // ATC button
      const atcBtn = this.querySelector('.ctl-product-card__atc');
      if (atcBtn) {
        atcBtn.addEventListener('click', () => this.addToCart());
      }

      // Wishlist button
      const wishlistBtn = this.querySelector('.ctl-product-card__wishlist .wishlist__button');
      if (wishlistBtn) {
        wishlistBtn.addEventListener('click', (e) => this.toggleWishlist(e));
      }

      // Let iWish mark already-wishlisted items
      this.initWishlist();
    }
  }

  customElements.define('create-the-look-product', CreateTheLookProduct);
}
